import React, { useEffect, useState } from 'react';
// import { v4 as uuid } from 'uuid';
import CodeBlock from '@tiptap/extension-code-block';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { decode } from 'html-entities';
import './RichTextEditor.css';

import {
  BorderBottom,
  BorderRight,
  Brush,
  Code,
  Delete,
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatUnderlined,
  HighlightAlt,
  Image as ImageIcon,
  Link as LinkIcon,
  Redo,
  TableChart,
  Undo,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { extractTextContent, stripCommentMarkers } from '../../utils';
import ShowToast, { ToastTypeEnum } from '../ShowToast';
import { CommentAPIService } from './CommentExtension/CommentAPIService';
import { CommentExtension } from './CommentExtension/CommentExtension';
import { CommentMark } from './CommentExtension/CommentMark';
import CommentPopup from './CommentExtension/CommentPopup';
import CommentThreadComponent from './CommentExtension/CommentThread';
import { Comment, CommentThread } from './CommentExtension/CommentTypes';

export interface ToolbarOptions {
  fontFamily?: boolean;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  textColor?: boolean;
  highlight?: boolean;
  bulletList?: boolean;
  orderedList?: boolean;
  alignLeft?: boolean;
  alignCenter?: boolean;
  alignRight?: boolean;
  link?: boolean;
  image?: boolean;
  codeBlock?: boolean;
  undo?: boolean;
  redo?: boolean;
  table?: boolean;
}

const defaultToolbarOptions: ToolbarOptions = {
  fontFamily: true,
  bold: true,
  italic: true,
  underline: true,
  textColor: true,
  highlight: true,
  bulletList: true,
  orderedList: true,
  alignLeft: true,
  alignCenter: true,
  alignRight: true,
  link: true,
  image: true,
  codeBlock: true,
  undo: true,
  redo: true,
  table: true,
};

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  toolbarOptions?: ToolbarOptions;
  showToolbarOnFocus?: boolean;
  maxHeight?: string | number;
  height?: string | number;
  width?: string | number;
  readOnly?: boolean;

  commentAPI?: any;
  enableComments?: boolean;

  solutionId?: string;
  orgId?: string;
  processStatus?: string;
  subProcessStatus?: string;
  syncContentOnComment?: () => void;
  fontSize?: string;
  isVersionViewMode?: boolean;
  selectedThreadId?: string;
  setSelectedThreadId?: (threadId: string) => void;
  disableHover?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder = 'Write something...',
  toolbarOptions = defaultToolbarOptions,
  showToolbarOnFocus,
  maxHeight = 250,
  height = 'auto',
  width = '100%',
  readOnly = false,
  enableComments = false,

  solutionId = '',
  orgId = '',
  processStatus = '',
  subProcessStatus = '',
  fontSize = 'text-medium',
  syncContentOnComment = () => {},
  isVersionViewMode = false,
  selectedThreadId,
  setSelectedThreadId = () => {},
  disableHover = false,
}) => {
  const [textColor, setTextColor] = useState('#000000');
  const [highlightColor, setHighlightColor] = useState('#ffffff');
  const [isFocused, setIsFocused] = useState(false);

  const [commentThreads, setCommentThreads] = useState<Map<string, CommentThread>>(new Map());
  const [showCommentPopup, setShowCommentPopup] = useState(false);
  const [showCommentThread, setShowCommentThread] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [selectionTimer, setSelectionTimer] = useState<NodeJS.Timeout | null>(null);

  const options = { ...defaultToolbarOptions, ...toolbarOptions };
  const shouldShowToolbar = !readOnly && (showToolbarOnFocus === true ? isFocused : true);

  const [commentAPIService] = useState(() => new CommentAPIService());
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  // keep track of last values we notified parent about
  const lastSentRawRef = React.useRef<string>(content || '');
  const lastSentTextRef = React.useRef<string>(
    (extractTextContent(stripCommentMarkers(content || '')) || '').trim()
  );
  const htmlRef = React.useRef<string>(content || '');
  const previousHtmlRef = React.useRef<string>(content || '');

  // keep refs in sync when content prop changes from parent
  useEffect(() => {
    lastSentRawRef.current = content || '';
    lastSentTextRef.current = (extractTextContent(stripCommentMarkers(content || '')) || '').trim();
    htmlRef.current = content || '';
  }, [content]);

  const getStrippedHtml = (htmlStr: string) => stripCommentMarkers(htmlStr || '');
  const getTextFromHtml = (htmlStr: string) =>
    (extractTextContent(getStrippedHtml(htmlStr)) || '').trim();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      CodeBlock,
      FontFamily.configure({ types: ['textStyle'] }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Image.configure({ inline: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      CommentMark,
      ...(enableComments ? [CommentExtension.configure()] : []),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
        showOnlyWhenEditable: false,
      }),
    ],
    content,
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: `editor-content ${fontSize} ${readOnly ? 'read-only' : ''}`,
      },
      handleKeyDown: (view, event) => {
        const { state } = view;
        const { selection } = state;
        const { $from } = selection;

        if (selection.empty) {
          const marks = state.storedMarks || $from.marks();

          const hasHighlight = marks?.some((m) => m.type.name === 'highlight');
          const hasComment = marks?.some((m) => m.type.name === 'commentMark');

          if (hasHighlight || hasComment) {
            const node = $from.parent;
            const atEnd = $from.parentOffset === node.content.size;

            if (atEnd) {
              event.preventDefault();

              view.dispatch(state.tr.insertText(''));

              const tr = view.state.tr;
              if (hasHighlight) {
                tr.removeStoredMark(view.state.schema.marks.highlight);
              }
              if (hasComment) {
                tr.removeStoredMark(view.state.schema.marks.commentMark);
              }
              view.dispatch(tr);
              return true;
            }
          }
        }
        return false;
      },
      handleClick: enableComments
        ? (_view, _pos, event) => {
            const target = event.target as HTMLElement;
            if (target?.classList?.contains('comment-marker')) {
              if (isLoadingComments) {
                ShowToast({
                  message: 'Comments are still loading, please wait...',
                  type: ToastTypeEnum.Success,
                });
                return true;
              }

              const commentIds = target.getAttribute('data-comment-ids')?.split(',') || [];
              const firstThreadId = commentIds[0];

              if (firstThreadId && commentThreads.has(firstThreadId)) {
                event.preventDefault();
                event.stopPropagation();

                const rect = target.getBoundingClientRect();
                const isMobile = window.innerWidth <= 768;
                if (isMobile) {
                  const desiredTop = rect.bottom + 8;
                  const maxTop = Math.max(8, Math.min(desiredTop, window.innerHeight - 320)); // keep within viewport
                  setPopupPosition({
                    top: maxTop,
                    left: Math.min(rect.left, window.innerWidth - 360),
                  });
                } else {
                  setPopupPosition({
                    top: rect.bottom + 8 + window.scrollY,
                    left: Math.min(rect.left + window.scrollX, window.innerWidth - 360),
                  });
                }

                setSelectedText(target.textContent || '');
                setActiveThreadId(firstThreadId);
                setShowCommentThread(true);
                setShowCommentPopup(false);
                return true;
              }
            }

            if (!target?.closest('.comment-thread') && !target?.closest('.comment-popup')) {
              setTimeout(() => {
                if (!readOnly && !editor?.state.selection.empty) return;
                closeCommentPopups();
              }, 50);
            }

            return false;
          }
        : undefined,
    },
    onUpdate: ({ editor }) => {
      const rawNew = editor.getHTML();
      const strippedNew = getStrippedHtml(rawNew);
      const textNew = getTextFromHtml(rawNew);

      const lastRaw = lastSentRawRef.current;
      const lastText = lastSentTextRef.current;
      const strippedLast = getStrippedHtml(lastRaw);

      // Always keep the internal ref in sync (no state update)
      htmlRef.current = rawNew;

      // 1) If textual content changed (covers clearing, typing, paste, etc.)
      if (textNew !== lastText) {
        lastSentRawRef.current = rawNew;
        lastSentTextRef.current = textNew;
        // call parent with raw HTML (preserves markup)
        onChange(rawNew);
        return;
      }

      // 2) Text is same (ignores plain text differences). Check for HTML/comment-only/format changes:
      if (rawNew !== lastRaw) {
        // comment-only change? (stripped HTML equal, but raw differs)
        if (enableComments && strippedNew === strippedLast) {
          lastSentRawRef.current = rawNew;
          // text hasn't changed but comment markers did -> save
          onChange(rawNew);
          return;
        }

        // formatting or markup change with non-empty text -> save
        // but avoid saving trivial empty-html differences (like initial <p></p>)
        if (textNew !== '') {
          lastSentRawRef.current = rawNew;
          lastSentTextRef.current = textNew; // same value but keep refs consistent
          onChange(rawNew);
          return;
        }

        // else: both text empty and only HTML wrapper differences => ignore (prevents initial autosave)
      }

      // if we get here, nothing meaningful to notify parent about
    },
    onSelectionUpdate: ({ editor }) => {
      const color = editor.getAttributes('textStyle').color || '#000000';
      const highlight = editor.getAttributes('highlight').color || '#ffffff';
      setTextColor(color);
      setHighlightColor(highlight);

      if (!enableComments) return;

      const { from, to } = editor.state.selection;

      if (selectionTimer) {
        clearTimeout(selectionTimer);
        setSelectionTimer(null);
      }

      if (from !== to && !editor.state.selection.empty) {
        const text = editor.state.doc.textBetween(from, to);
        if (text.trim().length > 0) {
          if (showCommentPopup || showCommentThread) {
            return;
          }

          setSelectedText(text);

          const timer = setTimeout(() => {
            try {
              const currentSelection = editor.state.selection;
              if (
                currentSelection.from === from &&
                currentSelection.to === to &&
                !currentSelection.empty
              ) {
                const view = editor.view;
                const start = view.coordsAtPos(from);
                const isMobile = window.innerWidth <= 768;
                if (isMobile) {
                  const desiredTop = start.bottom + 8;
                  const maxTop = Math.max(8, Math.min(desiredTop, window.innerHeight - 280));
                  setPopupPosition({
                    top: maxTop,
                    left: Math.min(start.left, window.innerWidth - 320),
                  });
                } else {
                  setPopupPosition({
                    top: start.bottom + window.scrollY + 8,
                    left: Math.min(start.left + window.scrollX, window.innerWidth - 320),
                  });
                }

                setTimeout(() => {
                  const stillSelected = !editor.state.selection.empty;
                  if (stillSelected) {
                    setShowCommentPopup(true);
                    setShowCommentThread(false);
                    setActiveThreadId(null);
                  }
                }, 100);
              }
            } catch (error) {
              console.warn('Error handling selection:', error);
            }
          }, 200);

          setSelectionTimer(timer);
        }
      } else {
        // Close popup when selection is lost
        if (showCommentPopup && !showCommentThread) {
          setShowCommentPopup(false);
        }
      }
    },
    onFocus: () => {
      setIsFocused(true);
    },
    onBlur: () => {
      setIsFocused(false);
    },
  });

  // Sync editor content when content prop changes
  // useEffect(() => {
  //   if (editor && content !== html) {
  //     editor.commands.setContent(content);
  //   }
  // }, [content, editor, html]);

  // Sync editor content when content prop changes.
  // If the parent-provided HTML differs from what the editor currently shows,
  // update the editor without emitting another onUpdate. This keeps external
  // updates (e.g. DataXPLR merges) in sync with the TipTap state.
  useEffect(() => {
    if (!editor) return;

    const editorHTML = editor.getHTML();

    if (content !== editorHTML) {
      editor.commands.setContent(content, false);
    }
  }, [content, editor]);

  useEffect(() => {
    return () => {
      if (selectionTimer) {
        clearTimeout(selectionTimer);
      }
    };
  }, [selectionTimer]);

  // API integration functions
  const createCommentViaAPI = async (text: string, replyTo?: string) => {
    if (!solutionId || !processStatus) return null;

    try {
      if (activeThreadId) {
        // EXISTING THREAD: Use the current activeThreadId
        const result = await commentAPIService.addComment({
          thread_id: activeThreadId, // This should be the backend thread ID
          text,
          parent_comment_id: replyTo || null,
        });

        return {
          id: result.id,
          text: result.text,
          author: result.user_email,
          authorId: result.user_email,
          timestamp: new Date(result.created_at),
          user_email: result.user_email,
          parent_comment_id: result.parent_comment_id,
          depth: result.depth,
          replies: [],
        };
      } else {
        // NEW THREAD: Backend will generate the thread ID
        const result = await commentAPIService.createThread({
          solution_id: solutionId,
          org_id: orgId,
          process_status: processStatus,
          sub_process_status: subProcessStatus || '',
          selected_text: selectedText,
          text,
        });

        return {
          id: result.comments[0].id,
          text: result.comments[0].text,
          author: result.comments[0].user_email,
          authorId: result.comments[0].user_email,
          timestamp: new Date(result.comments[0].created_at),
          user_email: result.comments[0].user_email,
          parent_comment_id: null,
          depth: 0,
          replies: [],
          threadId: result.id, // Backend-generated thread ID
        };
      }
    } catch (error) {
      console.error('Failed to create comment via API:', error);
      return null;
    }
  };

  const updateCommentViaAPI = async (commentId: string, text: string) => {
    try {
      const result = await commentAPIService.editComment({
        comment_id: commentId,
        text,
      });

      return {
        id: result.id,
        text: result.text,
        author: result.user_email,
        authorId: result.user_email,
        timestamp: new Date(result.updated_at),
      };
    } catch (error) {
      console.error('Failed to update comment via API:', error);
      return null;
    }
  };

  const deleteCommentViaAPI = async (commentId: string) => {
    try {
      const result = await commentAPIService.deleteComment(commentId);
      return result.success;
    } catch (error) {
      console.error('Failed to delete comment via API:', error);
      return false;
    }
  };

  const addComment = async (text: string, replyTo?: string) => {
    setIsLoading(true);
    try {
      const apiComment = await createCommentViaAPI(text, replyTo);

      if (!apiComment) {
        console.error('Failed to create comment');
        return;
      }

      setCommentThreads((currentThreads) => {
        const newThreads = new Map(currentThreads);

        if (apiComment.threadId && !activeThreadId) {
          // NEW THREAD: Use backend-generated thread ID
          const backendThreadId = apiComment.threadId;
          const { threadId: _, ...commentWithoutThreadId } = apiComment;

          const commentThread: CommentThread = {
            id: backendThreadId,
            comments: [commentWithoutThreadId],
            selectedText,
          };

          newThreads.set(backendThreadId, commentThread);
          setActiveThreadId(backendThreadId);

          // Apply comment mark even in readonly mode
          setTimeout(() => {
            if (editor && !editor.isDestroyed) {
              editor.commands.setCommentMark(backendThreadId);
            }
          }, 100);
        } else if (activeThreadId && newThreads.has(activeThreadId)) {
          // EXISTING THREAD - trigger sync to update comment counts
          const existingThread = newThreads.get(activeThreadId)!;

          if (replyTo) {
            const findAndAddReply = (commentList: Comment[]): Comment[] => {
              return commentList.map((comment) => {
                if (comment.id === replyTo) {
                  return {
                    ...comment,
                    replies: [apiComment, ...(comment.replies || [])],
                  };
                } else if (comment.replies && comment.replies.length > 0) {
                  return {
                    ...comment,
                    replies: findAndAddReply(comment.replies),
                  };
                }
                return comment;
              });
            };
            existingThread.comments = findAndAddReply(existingThread.comments);
          } else {
            existingThread.comments = [apiComment, ...existingThread.comments];
          }

          newThreads.set(activeThreadId, existingThread);
        }

        return newThreads;
      });

      if (!replyTo) {
        setShowCommentPopup(false);
        setShowCommentThread(true);
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  function htmlToText(str: string) {
    // 1. Decode all HTML entities (including &#39; and &quot;)
    str = decode(str);

    // 2. Normalize weird spaces
    str = str
      .replace(/\u00A0/g, ' ') // non-breaking space
      .replace(/\u200B/g, ''); // zero-width space

    // 3. Remove all tags, but keep a space for each tag to avoid word collisions
    str = str.replace(/<[^>]*>/g, ' ');

    // 4. Collapse whitespace
    str = str.replace(/\s+/g, ' ');

    return str.trim();
  }

  useEffect(() => {
    const currentContentWithoutMarkers = stripCommentMarkers(previousHtmlRef.current);
    const newContentWithoutMarkers = stripCommentMarkers(content);

    const isOnlyCommentChange =
      htmlToText(currentContentWithoutMarkers) === htmlToText(newContentWithoutMarkers);

    if (
      enableComments &&
      readOnly &&
      isOnlyCommentChange &&
      content?.length > 0 &&
      !isVersionViewMode
    ) {
      setTimeout(() => {
        syncContentOnComment();
      }, 500);
    }

    previousHtmlRef.current = content;
  }, [content, enableComments, readOnly, isVersionViewMode, syncContentOnComment]);

  const editComment = async (commentId: string, newText: string) => {
    setIsLoading(true);
    try {
      await updateCommentViaAPI(commentId, newText);

      const updateCommentInThread = (commentList: Comment[]): Comment[] => {
        return commentList.map((comment) => {
          if (comment.id === commentId) {
            return { ...comment, text: newText };
          } else if (comment.replies) {
            return { ...comment, replies: updateCommentInThread(comment.replies) };
          }
          return comment;
        });
      };

      setCommentThreads((currentThreads) => {
        const newThreads = new Map(currentThreads);
        newThreads.forEach((thread) => {
          thread.comments = updateCommentInThread(thread.comments);
        });
        return newThreads;
      });
    } catch (error) {
      console.error('Failed to edit comment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteComment = async (commentId: string) => {
    setIsLoading(true);
    try {
      const success = await deleteCommentViaAPI(commentId);
      if (!success) return;

      const removeCommentFromTree = (commentList: Comment[]): Comment[] => {
        return commentList
          .filter((comment) => comment.id !== commentId)
          .map((comment) => ({
            ...comment,
            replies: comment.replies ? removeCommentFromTree(comment.replies) : [],
          }));
      };

      setCommentThreads((currentThreads) => {
        const newThreads = new Map(currentThreads);
        const threadsToRemove: string[] = [];

        newThreads.forEach((thread, threadId) => {
          thread.comments = removeCommentFromTree(thread.comments);

          const hasComments = thread.comments.some(
            (comment) => comment.id !== commentId || (comment.replies && comment.replies.length > 0)
          );

          if (!hasComments) {
            threadsToRemove.push(threadId);
          }
        });

        threadsToRemove.forEach((threadId) => {
          newThreads.delete(threadId);
          setTimeout(() => {
            editor?.commands?.removeCommentMark(threadId);
          }, 0);
        });

        return newThreads;
      });
    } catch (error) {
      console.error('Failed to delete comment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resolveThread = async () => {
    if (!activeThreadId) return;

    setIsLoading(true);

    try {
      const success = await commentAPIService.deleteThread(activeThreadId);
      if (success) {
        // Remove thread from state
        setCommentThreads((currentThreads) => {
          const newThreads = new Map(currentThreads);
          newThreads.delete(activeThreadId);
          return newThreads;
        });

        // Remove comment mark from editor
        editor?.commands?.removeCommentMark(activeThreadId);

        // Close popups
        closeCommentPopups();
      }
    } catch (error) {
      console.error('Failed to resolve thread:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const closeCommentPopups = () => {
    setShowCommentPopup(false);
    setShowCommentThread(false);
    setActiveThreadId(null);
    setSelectedText('');

    if (selectionTimer) {
      clearTimeout(selectionTimer);
      setSelectionTimer(null);
    }
  };

  // Rest of the component remains exactly the same...
  const setLink = () => {
    const previousUrl = editor?.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleToolbarMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const handleToolbarClick = (callback: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (!readOnly) {
      callback();
      editor?.commands.focus();
    }
  };

  const handleEditorContainerClick = (e: React.MouseEvent) => {
    if (readOnly) return;

    const target = e.target as HTMLElement;
    const editorContent = e.currentTarget.querySelector('.editor-content');

    if (editorContent && (editorContent.contains(target) || target === editorContent)) {
      if (!editor?.isFocused) {
        setTimeout(() => {
          editor?.commands.focus();
        }, 0);
      }
    }
  };
  const buildCommentTree = (flatComments: any[]): Comment[] => {
    const commentMap = new Map<string, Comment>();
    const rootComments: Comment[] = [];

    // First pass: create all comment objects
    flatComments.forEach((apiComment) => {
      const comment: Comment = {
        id: apiComment.id,
        text: apiComment.text,
        author: apiComment.user_email,
        authorId: apiComment.user_email,
        timestamp: new Date(apiComment.created_at),
        user_email: apiComment.user_email,
        parent_comment_id: apiComment.parent_comment_id,
        depth: apiComment.depth,
        replies: [],
      };
      commentMap.set(comment.id, comment);
    });

    // Second pass: build the tree structure
    flatComments.forEach((apiComment) => {
      const comment = commentMap.get(apiComment.id)!;

      if (apiComment.parent_comment_id && commentMap.has(apiComment.parent_comment_id)) {
        // This is a reply
        const parent = commentMap.get(apiComment.parent_comment_id)!;
        parent.replies = parent.replies || [];
        parent.replies.push(comment);
      } else {
        // This is a root comment
        rootComments.push(comment);
      }
    });

    return rootComments;
  };

  useEffect(() => {
    const loadExistingComments = async () => {
      if (!solutionId || !processStatus) return;

      setIsLoadingComments(true);
      try {
        const threads = await commentAPIService.getThreads(
          solutionId,
          processStatus,
          subProcessStatus
        );

        const threadMap = new Map<string, CommentThread>();
        threads.forEach((thread: any) => {
          const nestedComments = buildCommentTree(thread.comments);

          threadMap.set(thread.id, {
            id: thread.id,
            comments: nestedComments,
            selectedText: thread.selected_text,
            solution_id: thread.solution_id,
            process_status: thread.process_status,
            sub_process_status: thread.sub_process_status,
          });
        });

        setCommentThreads(threadMap);
      } catch (error) {
        console.error('Failed to load existing comments:', error);
      } finally {
        setIsLoadingComments(false);
      }
    };

    if (enableComments && editor) {
      loadExistingComments();
    }
  }, [solutionId, processStatus, subProcessStatus, enableComments, editor]);

  useEffect(() => {
    // Simple focus fix when switching from readonly to edit mode
    if (editor && !readOnly && isFocused) {
      setTimeout(() => {
        editor.commands.focus();
      }, 100);
    }
  }, [readOnly, editor, isFocused]);

  // Sync TipTap editor instance whenever readOnly changes
  useEffect(() => {
    if (!editor) return;
    if (editor.isDestroyed) return;

    // 1) update editable state (TipTap API)
    if (typeof editor.setEditable === 'function') {
      editor.setEditable(!readOnly);
    } else if (typeof (editor as any).setOptions === 'function') {
      // fallback for older/newer versions of TipTap
      (editor as any).setOptions({ editable: !readOnly });
    }

    // 2) update editorProps attributes so the class (read-only) is kept in sync
    if (typeof (editor as any).setOptions === 'function') {
      (editor as any).setOptions({
        editorProps: {
          attributes: {
            class: `editor-content ${readOnly ? 'read-only' : ''}`,
          },
        },
      });
    }
  }, [readOnly, editor]);

  useEffect(() => {
    if (selectedThreadId && editor) {
      // Find comment markers with this comment ID
      const commentMarkers = editor.view.dom.querySelectorAll('.comment-marker');

      commentMarkers.forEach((marker: Element) => {
        const commentIds = marker.getAttribute('data-comment-ids')?.split(',') || [];
        if (commentIds.includes(selectedThreadId)) {
          // Scroll to and highlight the marker
          marker.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          // Add highlight effect
          const htmlMarker = marker as HTMLElement;
          const originalBg = htmlMarker.style.backgroundColor;
          const originalBorder = htmlMarker.style.border;

          htmlMarker.style.backgroundColor = '#fff3cd';
          htmlMarker.style.border = '2px solid #ffc107';
          htmlMarker.style.transition = 'all 0.3s ease';

          // Remove highlight after 3 seconds
          setTimeout(() => {
            htmlMarker.style.backgroundColor = originalBg;
            htmlMarker.style.border = originalBorder;
          }, 3000);
        }
      });

      // Clear the selected comment after handling
      setTimeout(() => {
        setSelectedThreadId('');
      }, 100);
    }
  }, [selectedThreadId, editor]);

  if (!editor) return null;

  return (
    <>
      <div
        className={`rich-text-editor ${disableHover ? 'no-hover' : ''}`}
        style={{ height, width, maxHeight, display: 'flex', flexDirection: 'column' }}
        onClick={handleEditorContainerClick}
      >
        {shouldShowToolbar && (
          <div
            className="editor-toolbar"
            onMouseDown={handleToolbarMouseDown}
            style={{
              transition:
                showToolbarOnFocus === true
                  ? 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out'
                  : 'none',
              opacity: shouldShowToolbar ? 1 : 0,
              transform: shouldShowToolbar ? 'translateY(0)' : 'translateY(-10px)',
            }}
          >
            {/* All toolbar content remains exactly the same... */}
            {options.fontFamily && (
              <>
                <div className="toolbar-group">
                  <select
                    className="toolbar-select"
                    value={editor.getAttributes('textStyle').fontFamily || ''}
                    onChange={(e) => {
                      editor.chain().focus().setFontFamily(e.target.value).run();
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <option value="">Normal</option>
                    <option value="Arial, sans-serif">Sans Serif</option>
                    <option value="Georgia, serif">Serif</option>
                    <option value="monospace">Monospace</option>
                  </select>
                </div>
                <div className="toolbar-divider" />
              </>
            )}

            {(options.bold || options.italic || options.underline) && (
              <>
                <div className="toolbar-group">
                  {options.bold && (
                    <button
                      className={`toolbar-button ${editor.isActive('bold') ? 'is-active' : ''}`}
                      onClick={handleToolbarClick(() => editor.chain().focus().toggleBold().run())}
                      title="Bold"
                    >
                      <FormatBold fontSize="small" />
                    </button>
                  )}
                  {options.italic && (
                    <button
                      className={`toolbar-button ${editor.isActive('italic') ? 'is-active' : ''}`}
                      onClick={handleToolbarClick(() =>
                        editor.chain().focus().toggleItalic().run()
                      )}
                      title="Italic"
                    >
                      <FormatItalic fontSize="small" />
                    </button>
                  )}
                  {options.underline && (
                    <button
                      className={`toolbar-button ${editor.isActive('underline') ? 'is-active' : ''}`}
                      onClick={handleToolbarClick(() =>
                        editor.chain().focus().toggleUnderline().run()
                      )}
                      title="Underline"
                    >
                      <FormatUnderlined fontSize="small" />
                    </button>
                  )}
                </div>
                <div className="toolbar-divider" />
              </>
            )}

            {(options.textColor || options.highlight) && (
              <>
                <div className="toolbar-group">
                  {options.textColor && (
                    <>
                      <span className="color-picker-label">
                        <Brush fontSize="small" />
                      </span>
                      <input
                        type="color"
                        className="color-input"
                        value={textColor}
                        onChange={(e) => {
                          editor.chain().focus().setColor(e.target.value).run();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    </>
                  )}
                  {options.highlight && (
                    <>
                      <span className="color-picker-label">
                        <HighlightAlt fontSize="small" />
                      </span>
                      <input
                        type="color"
                        className="color-input"
                        value={highlightColor}
                        onChange={(e) => {
                          editor
                            .chain()
                            .focus()
                            .setMark('highlight', { color: e.target.value })
                            .run();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    </>
                  )}
                </div>
                <div className="toolbar-divider" />
              </>
            )}

            {(options.bulletList || options.orderedList) && (
              <>
                <div className="toolbar-group">
                  {options.bulletList && (
                    <button
                      className={`toolbar-button ${editor.isActive('bulletList') ? 'is-active' : ''}`}
                      onClick={handleToolbarClick(() =>
                        editor.chain().focus().toggleBulletList().run()
                      )}
                      title="Bullet List"
                    >
                      <FormatListBulleted fontSize="small" />
                    </button>
                  )}
                  {options.orderedList && (
                    <button
                      className={`toolbar-button ${editor.isActive('orderedList') ? 'is-active' : ''}`}
                      onClick={handleToolbarClick(() =>
                        editor.chain().focus().toggleOrderedList().run()
                      )}
                      title="Ordered List"
                    >
                      <FormatListNumbered fontSize="small" />
                    </button>
                  )}
                </div>
                <div className="toolbar-divider" />
              </>
            )}

            {options.table && (
              <>
                <div className="toolbar-group">
                  <button
                    className="toolbar-button"
                    onClick={handleToolbarClick(() =>
                      editor
                        .chain()
                        .focus()
                        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                        .run()
                    )}
                    title="Insert Table"
                    disabled={!editor}
                  >
                    <TableChart fontSize="small" />
                  </button>

                  <button
                    className="toolbar-button"
                    onClick={handleToolbarClick(() =>
                      editor.chain().focus().addColumnAfter().run()
                    )}
                    title="Add Column After"
                    disabled={!editor.isActive('table')}
                  >
                    <BorderRight fontSize="small" />
                  </button>
                  <button
                    className="toolbar-button"
                    onClick={handleToolbarClick(() => editor.chain().focus().deleteColumn().run())}
                    title="Delete Column"
                  >
                    <BorderRight style={{ transform: 'rotate(180deg)' }} fontSize="small" />
                  </button>
                  <button
                    className="toolbar-button"
                    onClick={handleToolbarClick(() => editor.chain().focus().addRowAfter().run())}
                    title="Add Row After"
                    disabled={!editor.isActive('table')}
                  >
                    <BorderBottom fontSize="small" />
                  </button>
                  <button
                    className="toolbar-button"
                    onClick={handleToolbarClick(() => editor.chain().focus().deleteRow().run())}
                    title="Delete Row"
                  >
                    <BorderBottom style={{ transform: 'rotate(180deg)' }} fontSize="small" />
                  </button>
                  <button
                    className="toolbar-button"
                    onClick={handleToolbarClick(() => editor.chain().focus().deleteTable().run())}
                    title="Delete Table"
                    disabled={!editor.isActive('table')}
                  >
                    <Delete fontSize="small" />
                  </button>
                </div>
                <div className="toolbar-divider" />
              </>
            )}

            {(options.alignLeft || options.alignCenter || options.alignRight) && (
              <>
                <div className="toolbar-group">
                  {options.alignLeft && (
                    <button
                      className={`toolbar-button ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}`}
                      onClick={handleToolbarClick(() =>
                        editor.chain().focus().setTextAlign('left').run()
                      )}
                      title="Align Left"
                    >
                      <FormatAlignLeft fontSize="small" />
                    </button>
                  )}
                  {options.alignCenter && (
                    <button
                      className={`toolbar-button ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}`}
                      onClick={handleToolbarClick(() =>
                        editor.chain().focus().setTextAlign('center').run()
                      )}
                      title="Align Center"
                    >
                      <FormatAlignCenter fontSize="small" />
                    </button>
                  )}
                  {options.alignRight && (
                    <button
                      className={`toolbar-button ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}`}
                      onClick={handleToolbarClick(() =>
                        editor.chain().focus().setTextAlign('right').run()
                      )}
                      title="Align Right"
                    >
                      <FormatAlignRight fontSize="small" />
                    </button>
                  )}
                </div>
                <div className="toolbar-divider" />
              </>
            )}

            {(options.link || options.image || options.codeBlock) && (
              <>
                <div className="toolbar-group">
                  {options.link && (
                    <button
                      className={`toolbar-button ${editor.isActive('link') ? 'is-active' : ''}`}
                      onClick={handleToolbarClick(setLink)}
                      title="Insert Link"
                    >
                      <LinkIcon fontSize="small" />
                    </button>
                  )}
                  {options.image && (
                    <button
                      className="toolbar-button"
                      onClick={handleToolbarClick(() => {
                        const url = window.prompt('Enter image URL');
                        if (url) editor.chain().focus().setImage({ src: url }).run();
                      })}
                      title="Insert Image"
                    >
                      <ImageIcon fontSize="small" />
                    </button>
                  )}
                  {options.codeBlock && (
                    <button
                      className={`toolbar-button ${editor.isActive('codeBlock') ? 'is-active' : ''}`}
                      onClick={handleToolbarClick(() =>
                        editor.chain().focus().toggleCodeBlock().run()
                      )}
                      title="Code Block"
                    >
                      <Code fontSize="small" />
                    </button>
                  )}
                </div>
                <div className="toolbar-divider" />
              </>
            )}

            {(options.undo || options.redo) && (
              <div className="toolbar-group">
                {options.undo && (
                  <button
                    className="toolbar-button"
                    onClick={handleToolbarClick(() => editor.chain().focus().undo().run())}
                    disabled={!editor.can().undo()}
                    title="Undo"
                  >
                    <Undo fontSize="small" />
                  </button>
                )}
                {options.redo && (
                  <button
                    className="toolbar-button"
                    onClick={handleToolbarClick(() => editor.chain().focus().redo().run())}
                    disabled={!editor.can().redo()}
                    title="Redo"
                  >
                    <Redo fontSize="small" />
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '6px',
          }}
        >
          <EditorContent
            editor={editor}
            style={{
              flex: 1,
              minHeight: 0,
              borderRadius: '6px',
              maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
              overflowY: 'auto',
            }}
          />
        </div>

        {enableComments && isLoadingComments && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(255, 255, 255, 1)',
              padding: '10px 20px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '14px',
              color: '#666',
              zIndex: 1000,
            }}
          >
            <CircularProgress size={14} style={{ color: '#666' }} /> Loading comments...
          </div>
        )}

        {enableComments && showCommentPopup && selectedText && (
          <CommentPopup
            onAddComment={addComment}
            onClose={closeCommentPopups}
            position={popupPosition}
            selectedText={selectedText}
            isVisible={showCommentPopup}
            isLoading={isLoading}
          />
        )}

        {enableComments &&
          showCommentThread &&
          activeThreadId &&
          commentThreads.has(activeThreadId) && (
            <CommentThreadComponent
              comments={commentThreads.get(activeThreadId)!.comments}
              onAddComment={addComment}
              onEditComment={editComment}
              onDeleteComment={deleteComment}
              onResolveThread={resolveThread}
              onClose={closeCommentPopups}
              position={popupPosition}
              isLoading={isLoading}
              isResolving={isLoading}
            />
          )}
      </div>
    </>
  );
};

export default RichTextEditor;
